import React, { useRef, useEffect, useState } from "react";
import { X, Maximize2, GripVertical, Volume2, VolumeX } from "lucide-react";

export default function MonitorWindow({ user, stream, index = 0, onClose }) {
  const vref = useRef(null);
  const boxRef = useRef(null);
  const drag = useRef(null);
  const resize = useRef(null);
  const [pos, setPos] = useState({ x: 90 + index * 44, y: 130 + index * 44 });
  const [size, setSize] = useState({ w: 360, h: 300 });
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (vref.current && stream) {
      vref.current.srcObject = stream;
      vref.current.play?.().catch(() => {});
    }
  }, [stream]);

  useEffect(() => { if (vref.current) vref.current.muted = muted; }, [muted, stream]);

  // ---- drag to move (header) ----
  const onMove = (e) => {
    if (!drag.current) return;
    setPos({ x: drag.current.ox + (e.clientX - drag.current.sx), y: drag.current.oy + (e.clientY - drag.current.sy) });
  };
  const onUp = () => {
    drag.current = null;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
  };
  const onDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ---- resize by dragging edges/corner ----
  const MIN_W = 260, MIN_H = 220;
  const onResizeMove = (e) => {
    if (!resize.current) return;
    const { dir, sx, sy, ow, oh } = resize.current;
    let w = ow, h = oh;
    if (dir.includes("e")) w = Math.max(MIN_W, ow + (e.clientX - sx));
    if (dir.includes("s")) h = Math.max(MIN_H, oh + (e.clientY - sy));
    setSize({ w, h });
  };
  const onResizeUp = () => {
    resize.current = null;
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", onResizeUp);
  };
  const startResize = (dir) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    resize.current = { dir, sx: e.clientX, sy: e.clientY, ow: size.w, oh: size.h };
    window.addEventListener("mousemove", onResizeMove);
    window.addEventListener("mouseup", onResizeUp);
  };

  const fs = () => { if (boxRef.current?.requestFullscreen) boxRef.current.requestFullscreen(); };

  return (
    <div ref={boxRef} style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
      className="fixed z-[70] flex flex-col bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
      data-testid={`monitor-window-${user.user_id}`}>
      <div onMouseDown={onDown} className="shrink-0 cursor-move flex items-center justify-between px-3 py-2 bg-[#1c1c1c] border-b border-border select-none">
        <span className="text-sm font-medium flex items-center gap-1 truncate">
          <GripVertical className="w-4 h-4 text-muted-foreground" /> {user.name}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => setMuted((m) => !m)} data-testid={`monitor-mute-${user.user_id}`} className="p-1 text-muted-foreground hover:text-primary" title={muted ? "Unmute" : "Mute"}>
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button onClick={fs} data-testid={`monitor-fs-${user.user_id}`} className="p-1 text-muted-foreground hover:text-primary"><Maximize2 className="w-4 h-4" /></button>
          <button onClick={() => onClose(user.user_id)} data-testid={`monitor-close-${user.user_id}`} className="p-1 text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="relative flex-1 min-h-0 bg-black">
        {stream ? (
          <video ref={vref} autoPlay playsInline className="w-full h-full object-contain bg-black" data-testid={`monitor-video-${user.user_id}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Connecting to camera…</div>
        )}
      </div>

      <div className="shrink-0 px-3 py-1.5 text-xs text-muted-foreground truncate border-t border-border">{user.email}</div>

      {/* resize handles */}
      <div onMouseDown={startResize("e")} className="absolute top-8 bottom-3 right-0 w-1.5 cursor-ew-resize" />
      <div onMouseDown={startResize("s")} className="absolute left-3 right-3 bottom-0 h-1.5 cursor-ns-resize" />
      <div onMouseDown={startResize("se")} data-testid={`monitor-resize-${user.user_id}`}
        className="absolute right-0 bottom-0 w-4 h-4 cursor-nwse-resize"
        style={{ background: "linear-gradient(135deg, transparent 50%, #666 50%, #666 60%, transparent 60%, transparent 70%, #666 70%, #666 80%, transparent 80%)" }} />
    </div>
  );
}
